import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UploadCloud, FileText, CheckCircle2, Loader2, ArrowRight, X, Building2, Shield, Banknote, FileSignature, Stamp, LineChart, Calculator, ReceiptText, File, Link2, Sparkles, HelpCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useLocaleFormat } from "../i18n/useLocaleFormat";
import { resolveTypeSource } from "./uploadTypeSource";

const STATUS = {
  IDLE: "idle",
  SELECTED: "selected",   // iter64 — file picked + detection done, awaiting confirm
  UPLOADING: "uploading",
  UPLOADED: "uploaded",
  QUEUED: "queued",
  ANALYZING: "analyzing",
  COMPLETED: "completed",
  FAILED: "failed",
};

const DOC_TYPE_VALUES = [
  { value: "lease", Icon: Building2 },
  { value: "insurance", Icon: Shield },
  { value: "financing", Icon: Banknote },
  { value: "contract", Icon: FileSignature },
  { value: "permit", Icon: Stamp },
  { value: "invoice", Icon: ReceiptText },
  { value: "financials", Icon: LineChart },
  { value: "other", Icon: File },
];

export default function Upload() {
  const { t } = useTranslation(["upload", "common"]);
  const navigate = useNavigate();
  const { formatBytes } = useLocaleFormat();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState(STATUS.IDLE);
  const [progress, setProgress] = useState(0);
  const [docId, setDocId] = useState(null);
  const [documentType, setDocumentType] = useState("lease");
  // Deep-link from the documents kebab menu — when present the newly-created
  // doc is automatically lineage-linked to the parent once the upload finishes.
  const [searchParams] = useSearchParams();
  const attachTo = searchParams.get("attach_to");
  const relationshipKind = searchParams.get("relationship_kind");
  const presetType = searchParams.get("doc_type");
  const isAttachFlow = !!(attachTo && relationshipKind);
  const [parentInfo, setParentInfo] = useState(null);
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState(null);
  // Iter64 — optimistic pre-classification result :
  //   null            = not yet attempted (no file selected)
  //   { ...result, dismissed: false } = banner visible
  //   { dismissed: true }             = banner cleared by user choice
  const [autoDetected, setAutoDetected] = useState(null);
  const userOverrodeRef = useRef(false);

  // Pre-select the type the parent wants us to upload (lease → amendment of
  // lease, insurance → endorsement of insurance, etc.).
  useEffect(() => {
    if (presetType) setDocumentType(presetType);
  }, [presetType]);

  // Fetch a light parent doc summary so the banner can show the filename.
  useEffect(() => {
    if (!attachTo) {
      setParentInfo(null);
      return;
    }
    api.get(`/documents/${attachTo}`).then((r) => {
      setParentInfo({
        id: r.data.id,
        filename: r.data.filename,
        document_type: r.data.document_type,
      });
    }).catch(() => setParentInfo({ id: attachTo, filename: null }));
  }, [attachTo]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  }, []);

  const handleFile = (f) => {
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      toast.error(t("upload:errors.only_pdf"));
      return;
    }
    if (f.size > 25 * 1024 * 1024) {
      toast.error(t("upload:errors.too_large"));
      return;
    }
    setFile(f);
    setStatus(STATUS.SELECTED);
    // Iter64 — deterministic pre-classification ($0, <50 ms) runs while the
    // file is in SELECTED state. The user sees the cards + banner and can
    // override before confirming. The actual upload starts only when they
    // click "Téléverser maintenant".
    void detectType(f);
  };

  const confirmUpload = () => {
    if (!file) return;
    void upload(file);
  };

  // Returns the detected type (string | null). null = no override applied.
  const detectType = async (f) => {
    if (!f?.name) return null;
    userOverrodeRef.current = false;
    try {
      const res = await api.post("/documents/detect-type", {
        filename: f.name,
        text_preview: null,   // pdfjs-dist not in deps → filename-only path
      });
      const detected = res.data || {};
      // Don't override an explicit deep-link preset from the kebab menu.
      if (presetType) {
        setAutoDetected({ ...detected, dismissed: true });
        return null;
      }
      if (userOverrodeRef.current) {
        setAutoDetected({ ...detected, dismissed: true });
        return null;
      }
      const ok = (detected.confidence ?? 0) >= 0.65
        && detected.type && detected.type !== "other";
      if (ok) {
        setDocumentType(detected.type);
        setAutoDetected({ ...detected, dismissed: false });
        return detected.type;
      }
      setAutoDetected({ ...detected, dismissed: false });
      return null;
    } catch {
      setAutoDetected(null);
      return null;
    }
  };

  // Wrapper used by the doc-type cards — clicking a card always clears the
  // auto-detection banner (the user has spoken).
  const handlePickType = (value) => {
    userOverrodeRef.current = true;
    setDocumentType(value);
    if (autoDetected && !autoDetected.dismissed) {
      setAutoDetected((prev) => (prev ? { ...prev, dismissed: true } : prev));
    }
  };

  const pollTimerRef = useRef(null);

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const startPolling = (id) => {
    stopPolling();
    pollTimerRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/documents/${id}/status`);
        const s = res.data?.status;
        if (s === "queued") setStatus(STATUS.QUEUED);
        else if (s === "analyzing") setStatus(STATUS.ANALYZING);
        else if (s === "completed") {
          stopPolling();
          setStatus(STATUS.COMPLETED);
          toast.success(t("upload:toasts.complete"));
          // Auto-link as soon as the analysis completes so the child doc is
          // tied to its parent (chain assembled before the user navigates).
          if (isAttachFlow) {
            void linkLineage(id);
          }
        } else if (s === "failed") {
          stopPolling();
          setStatus(STATUS.FAILED);
          toast.error(res.data?.analysis_error || t("upload:toasts.failed"));
        }
      } catch (err) {
        console.error(err);
      }
    }, 2000);
  };

  const linkLineage = async (childDocId) => {
    if (!attachTo || !relationshipKind || !childDocId) return;
    setLinking(true);
    setLinkError(null);
    try {
      await api.post(`/documents/${childDocId}/lineage`, {
        parent_doc_id: attachTo,
        relationship_kind: relationshipKind,
      });
      toast.success(t("upload:lineage.link_success"));
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      setLinkError(detail);
      toast.error(`${t("upload:lineage.link_failed")} — ${detail}`);
    } finally {
      setLinking(false);
    }
  };

  useEffect(() => () => stopPolling(), []);

  const upload = async (f) => {
    setStatus(STATUS.UPLOADING);
    setProgress(0);
    try {
      const form = new FormData();
      form.append("file", f);
      // Use the current documentType (possibly auto-detected, possibly
      // overridden by user clicks on the cards before they hit Confirm).
      form.append("document_type", documentType);
      form.append("document_type_source", resolveTypeSource({
        userOverrode: userOverrodeRef.current,
        presetType,
      }));
      const res = await api.post("/documents/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) {
            setProgress(Math.round((e.loaded / e.total) * 100));
          }
        },
      });
      setDocId(res.data.id);
      setStatus(STATUS.UPLOADED);
      try {
        await api.post(`/documents/${res.data.id}/analyze`);
        setStatus(STATUS.QUEUED);
        startPolling(res.data.id);
      } catch (err) {
        const detail = err.response?.data?.detail;
        if (err.response?.status === 402) {
          toast.error(detail || t("upload:errors.monthly_limit"));
          setStatus(STATUS.FAILED);
        } else {
          toast.error(detail || t("upload:errors.could_not_start"));
          setStatus(STATUS.FAILED);
        }
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || t("upload:errors.upload_failed"));
      reset();
    }
  };

  const reset = () => {
    stopPolling();
    setFile(null);
    setStatus(STATUS.IDLE);
    setProgress(0);
    setDocId(null);
    setAutoDetected(null);
    userOverrodeRef.current = false;
  };

  // Per-doc-type dropzone headline. Falls back to the generic default when a
  // specific key is missing (i18n cushions any future doc-type addition).
  const dropzoneHeadline = t(
    `upload:dropzone.headline_${documentType}`,
    { defaultValue: t("upload:dropzone.headline_default") },
  );

  return (
    <div className="animate-fade-up" data-testid="upload-page">
      <div className="max-w-2xl mx-auto">
        {isAttachFlow && (
          <div
            className="mb-6 p-4 rounded-xl border border-amber-200 bg-amber-50 flex items-start gap-3"
            data-testid="upload-attach-banner"
          >
            <Link2 size={18} strokeWidth={1.75} className="text-amber-700 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-950">
                {t(`upload:lineage.banner_title_${relationshipKind}`, {
                  defaultValue: t("upload:lineage.banner_title_default"),
                })}
              </p>
              <p className="text-[12.5px] text-amber-900/80 mt-0.5 leading-relaxed truncate">
                {t("upload:lineage.banner_subtitle", {
                  filename: parentInfo?.filename || attachTo,
                })}
              </p>
              {linking && (
                <p className="text-[11.5px] text-amber-900/70 mt-1 flex items-center gap-1">
                  <Loader2 size={11} className="animate-spin" />
                  {t("upload:lineage.linking")}
                </p>
              )}
              {linkError && (
                <p className="text-[11.5px] text-red-700 mt-1" data-testid="upload-link-error">
                  {t("upload:lineage.link_failed")} — {linkError}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => navigate("/documents")}
              data-testid="upload-attach-cancel"
              className="text-amber-700 hover:text-amber-900 transition-colors"
              aria-label={t("common:actions.cancel")}
            >
              <X size={15} />
            </button>
          </div>
        )}
        <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-2">{t("upload:page_kicker")}</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950">
          {t("upload:page_title")}
        </h1>
        <p className="text-sm text-zinc-500 mt-1.5">{t("upload:page_subtitle")}</p>

        {(status === STATUS.IDLE || status === STATUS.SELECTED) && (
          <div className="mt-8" data-testid="doc-type-selector">
            <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-3">{t("upload:doc_type_label")}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {DOC_TYPE_VALUES.map(({ value, Icon }) => {
                const selected = documentType === value;
                return (
                  <button
                    key={value}
                    onClick={() => handlePickType(value)}
                    data-testid={`doc-type-${value}`}
                    className={`text-left p-4 rounded-xl border-2 transition-colors ${
                      selected
                        ? "border-zinc-950 bg-zinc-50"
                        : "border-zinc-200 bg-white hover:border-zinc-300"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <Icon size={16} strokeWidth={1.75} className={selected ? "text-zinc-950" : "text-zinc-500"} />
                      <span className={`text-sm font-semibold ${selected ? "text-zinc-950" : "text-zinc-700"}`}>
                        {t(`upload:doc_types.${value}.label`)}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-500 leading-relaxed">
                      {t(`upload:doc_types.${value}.description`)}
                    </p>
                  </button>
                );
              })}
            </div>

            {/* Iter64 — auto-detection banner. Visible only between the time
                a file is picked and the time the user either dismisses it
                (by clicking a different card) or restarts the upload. */}
            {autoDetected && !autoDetected.dismissed && (
              (autoDetected.confidence ?? 0) >= 0.65 &&
              autoDetected.type && autoDetected.type !== "other" ? (
                <div
                  className="mt-3 p-3 rounded-lg border border-amber-200 bg-amber-50 flex items-start gap-2"
                  data-testid="detect-banner-auto"
                >
                  <Sparkles size={14} strokeWidth={1.75} className="text-amber-700 mt-0.5 shrink-0" />
                  <p className="text-[12.5px] text-amber-950 leading-relaxed">
                    {t("upload:detect.type_detected_hint", {
                      label: t(`upload:doc_types.${autoDetected.type}.label`),
                    })}
                  </p>
                </div>
              ) : (
                <div
                  className="mt-3 p-3 rounded-lg border border-zinc-200 bg-zinc-50 flex items-start gap-2"
                  data-testid="detect-banner-no-signal"
                >
                  <HelpCircle size={14} strokeWidth={1.75} className="text-zinc-500 mt-0.5 shrink-0" />
                  <p className="text-[12.5px] text-zinc-600 leading-relaxed">
                    {t("upload:detect.no_signal")}
                  </p>
                </div>
              )
            )}

            {documentType === "financials" && (
              <div
                className="mt-4 p-4 rounded-xl border border-indigo-200 bg-indigo-50/50 flex items-start gap-3"
                data-testid="manual-financials-cta"
              >
                <Calculator size={18} strokeWidth={1.75} className="text-indigo-700 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-indigo-950">
                    {t("upload:manual_financials.title")}
                  </p>
                  <p className="text-xs text-indigo-900/70 mt-0.5 leading-relaxed">
                    {t("upload:manual_financials.subtitle")}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/financials/manual")}
                  data-testid="goto-manual-financials"
                  className="border-indigo-300 text-indigo-950 hover:bg-indigo-100"
                >
                  {t("upload:manual_financials.cta")} <ArrowRight size={12} className="ml-1" />
                </Button>
              </div>
            )}

            {documentType === "invoice" && (
              <div
                className="mt-4 p-4 rounded-xl border border-qenva-cat-invoice/30 bg-qenva-cat-invoice/5 flex items-start gap-3"
                data-testid="manual-invoice-cta"
              >
                <ReceiptText size={18} strokeWidth={1.75} className="text-qenva-cat-invoice mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-qenva-navy">
                    {t("upload:manual_invoice.title")}
                  </p>
                  <p className="text-xs text-zinc-600 mt-0.5 leading-relaxed">
                    {t("upload:manual_invoice.subtitle")}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/invoices/new")}
                  data-testid="goto-manual-invoice"
                  className="border-qenva-cat-invoice/40 text-qenva-cat-invoice hover:bg-qenva-cat-invoice/10"
                >
                  {t("upload:manual_invoice.cta")} <ArrowRight size={12} className="ml-1" />
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="mt-10">
          {status === STATUS.IDLE && (
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => inputRef.current?.click()}
              data-testid="dropzone"
              className={`rounded-2xl border-2 border-dashed p-12 sm:p-16 text-center cursor-pointer transition-colors ${
                dragOver
                  ? "border-zinc-950 bg-zinc-100"
                  : "border-zinc-300 bg-zinc-50 hover:bg-zinc-100"
              }`}
            >
              <input
                ref={inputRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                data-testid="file-input"
                onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
              />
              <div className="w-14 h-14 mx-auto rounded-xl bg-white border border-zinc-200 grid place-items-center mb-5 shadow-sm">
                <UploadCloud size={22} strokeWidth={1.5} className="text-zinc-700" />
              </div>
              <p className="font-display font-semibold text-lg text-zinc-950 tracking-tight">
                {dropzoneHeadline}
              </p>
              <p className="text-sm text-zinc-500 mt-1.5">{t("upload:dropzone.subline")}</p>
              <Button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  inputRef.current?.click();
                }}
                data-testid="browse-button"
                className="mt-6 h-10 bg-zinc-950 hover:bg-zinc-800 text-white rounded-lg"
              >
                {t("upload:dropzone.select_button")}
              </Button>
            </div>
          )}

          {status === STATUS.SELECTED && file && (
            <div
              data-testid="selected-confirm-card"
              className="bg-white border border-zinc-200 rounded-2xl p-6"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg bg-zinc-100 grid place-items-center shrink-0">
                  <FileText size={18} className="text-zinc-700" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm text-zinc-900 truncate" data-testid="selected-filename">
                    {file.name}
                  </p>
                  <p className="text-xs text-zinc-500 mt-0.5">{formatBytes(file.size)}</p>
                </div>
                <button
                  onClick={reset}
                  aria-label={t("common:actions.cancel")}
                  data-testid="selected-cancel"
                  className="w-8 h-8 grid place-items-center rounded-md hover:bg-zinc-100 text-zinc-500"
                >
                  <X size={15} />
                </button>
              </div>
              <p className="text-xs text-zinc-500 mb-4">
                {t("upload:selected.confirm_hint", {
                  type: t(`upload:doc_types.${documentType}.label`),
                })}
              </p>
              <Button
                onClick={confirmUpload}
                data-testid="confirm-upload-button"
                className="w-full h-11 bg-zinc-950 hover:bg-zinc-800 text-white rounded-lg group"
              >
                {t("upload:selected.confirm_button")}
                <ArrowRight size={15} className="ml-1.5 group-hover:translate-x-0.5 transition-transform" />
              </Button>
            </div>
          )}

          {status !== STATUS.IDLE && status !== STATUS.SELECTED && (
            <div
              data-testid="upload-progress-card"
              className="bg-white border border-zinc-200 rounded-2xl p-6"
            >
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-zinc-100 grid place-items-center shrink-0">
                    <FileText size={18} className="text-zinc-700" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-zinc-900 truncate" data-testid="upload-filename">
                      {file?.name}
                    </p>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {file ? formatBytes(file.size) : ""}
                    </p>
                  </div>
                </div>
                {status !== STATUS.COMPLETED && status !== STATUS.ANALYZING && status !== STATUS.QUEUED && (
                  <button
                    onClick={reset}
                    aria-label={t("common:actions.cancel")}
                    className="w-8 h-8 grid place-items-center rounded-md hover:bg-zinc-100 text-zinc-500"
                  >
                    <X size={15} />
                  </button>
                )}
              </div>

              {/* Progress bar */}
              <div className="space-y-3">
                <StepRow
                  active={status === STATUS.UPLOADING}
                  done={[STATUS.UPLOADED, STATUS.QUEUED, STATUS.ANALYZING, STATUS.COMPLETED].includes(status)}
                  label={status === STATUS.UPLOADING
                    ? t("upload:steps.uploading", { percent: progress })
                    : t("upload:steps.uploaded")}
                  testId="step-upload"
                />
                <StepRow
                  active={status === STATUS.QUEUED}
                  done={[STATUS.ANALYZING, STATUS.COMPLETED].includes(status)}
                  label={t("upload:steps.queued")}
                  pending={[STATUS.UPLOADING, STATUS.UPLOADED].includes(status)}
                  testId="step-queued"
                />
                <StepRow
                  active={status === STATUS.ANALYZING}
                  done={status === STATUS.COMPLETED}
                  label={t("upload:steps.analyzing")}
                  pending={[STATUS.UPLOADING, STATUS.UPLOADED, STATUS.QUEUED].includes(status)}
                  testId="step-analyze"
                />
                <StepRow
                  active={false}
                  done={status === STATUS.COMPLETED}
                  label={t("upload:steps.ready")}
                  pending={status !== STATUS.COMPLETED}
                  testId="step-ready"
                />
              </div>

              {status === STATUS.UPLOADING && (
                <div className="mt-5 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-zinc-950 transition-all"
                    style={{ width: `${progress}%` }}
                    data-testid="upload-progress-bar"
                  />
                </div>
              )}

              {(status === STATUS.QUEUED || status === STATUS.ANALYZING) && (
                <div className="mt-5" data-testid="analyzing-progress-bar">
                  <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden relative">
                    {/* Slow crawl 0→90% over ~20s using CSS keyframes ;
                        after the 20s the width stays at 90% until the real
                        `completed` status flips the layout. No % label —
                        purely visual reassurance. */}
                    <div
                      className="h-full bg-zinc-950 rounded-full"
                      style={{
                        animation: "qenva-analyze-crawl 20s ease-out forwards",
                      }}
                    />
                  </div>
                  <p className="text-[11.5px] text-zinc-500 mt-2 text-center">
                    {t("upload:analyzing_progress_label")}
                  </p>
                </div>
              )}

              {status === STATUS.FAILED && (
                <div
                  data-testid="upload-failed-banner"
                  className="mt-5 px-4 py-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-800"
                >
                  {t("upload:failed_banner")}
                </div>
              )}

              {(status === STATUS.COMPLETED || status === STATUS.FAILED) && (
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  {status === STATUS.COMPLETED && (
                    <Button
                      onClick={() => navigate(`/analysis/${docId}`)}
                      data-testid="view-analysis-button"
                      className="h-11 bg-zinc-950 hover:bg-zinc-800 text-white rounded-lg group flex-1"
                    >
                      {t("upload:actions.view_analysis")}
                      <ArrowRight size={15} className="ml-1.5 group-hover:translate-x-0.5 transition-transform" />
                    </Button>
                  )}
                  <Button
                    onClick={reset}
                    variant={status === STATUS.COMPLETED ? "outline" : "default"}
                    data-testid="upload-another-button"
                    className={`h-11 rounded-lg ${status === STATUS.FAILED ? "bg-zinc-950 hover:bg-zinc-800 text-white flex-1" : ""}`}
                  >
                    {t("upload:actions.upload_another")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StepRow({ active, done, label, pending, testId }) {
  return (
    <div className="flex items-center gap-3" data-testid={testId}>
      <div
        className={`w-6 h-6 rounded-full grid place-items-center shrink-0 ${
          done
            ? "bg-emerald-100 text-emerald-700"
            : active
            ? "bg-zinc-900 text-white"
            : "bg-zinc-100 text-zinc-400"
        }`}
      >
        {done ? (
          <CheckCircle2 size={14} strokeWidth={2} />
        ) : active ? (
          <Loader2 size={12} className="animate-spin" />
        ) : (
          <span className="text-[10px]">·</span>
        )}
      </div>
      <span
        className={`text-sm ${
          done ? "text-zinc-900" : active ? "text-zinc-900 font-medium" : pending ? "text-zinc-400" : "text-zinc-500"
        }`}
      >
        {label}
      </span>
    </div>
  );
}
